import React, { useState, useEffect } from "react";

import {
  Button,
  InfoPanel,
  Select,
  SelectData,
  TextField,
  Typography,
  useStepperDispatch,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useTypedSelector } from "../../../app/rootReducer";
import { useGetNationalities } from "../../../hooks/codebook/useGetNationalities";
import { useGetNationalMinorities } from "../../../hooks/codebook/useGetNationalMinorities";
import useInput from "../../../hooks/input/useInput";
import Child from "../../../models/Person/Child";
import ChildValidationSchema from "../../../models/Person/ChildValidationSchema";
import IntegrationResultStatusEnum from "../../../models/Person/IntegrationResultStatusEnum";
import CodeBook from "../../../models/Shared/CodeBook";
import {
  selectAllDocuments,
  updateRequestChild,
} from "../../../slices/requestSlice";
import { getChildSiblings } from "../../../slices/requestSlice";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import styles from "./ChildDataStep.module.scss";
import { useChildDocuments } from "./useChildDocuments";

export default function ChildDataStep() {
  const [child, setChild] = useState<Child>({} as Child);

  const childState = useTypedSelector((state) => state.request.request.child);

  const applicantParentOib = useTypedSelector(
    (state) => state.request.request.applicantParent.oib
  );

  const otherParentOib = useTypedSelector(
    (state) => state.request.request.otherParent?.oib
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (childState) {
      setChild(childState);
    }
    if (!childState.siblings) {
      dispatch(
        getChildSiblings({
          childOib: childState.oib,
          applicantParentOib: applicantParentOib,
          otherParentOib: otherParentOib,
        })
      );
    }
  }, [childState, applicantParentOib, otherParentOib, dispatch]);

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      return handleStep();
    },
  });

  const { handleMultipleChange } = useInput();

  const nationalities = useGetNationalities();
  const nationalMinorities = useGetNationalMinorities();

  useEffect(() => {
    if (childState) {
      if (childState.nationalityId == null) {
        const newChildState = {
          ...childState,
          nationalityId: 999,
          nationalityName: nationalities?.find(
            (nationality) => nationality.id === 999
          )?.name,
        };
        setChild(newChildState);
      } else {
        setChild(childState);
      }
    }
  }, [childState, nationalities]);

  const { t } = useTranslation();

  const [errors, doValidations] = useValidation({
    data: child,
    validationSchema: ChildValidationSchema,
  });

  const handleStep = () => {
    if (child) {
      const isValid = doValidations();
      if (isValid) {
        dispatch(updateRequestChild(child));
      } else {
        return isValid;
      }
    } else {
      return false;
    }
  };

  const emptyValue = "-";
  const allDocuments = useSelector(selectAllDocuments);

  const {
    isDocumentsModalOpen,
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleConfirmDocumentsModal,
    handleCloseDocumentsModal,
    handleToggleDocumentsModal,
    handleDownloadDocument,
    handleRemoveDocumentFromList,
    handleRemoveDocument,
  } = useChildDocuments({ setChild: setChild });

  const renderChildSiblings = () => {
    return child?.siblings && child.siblings.length > 0 ? (
      <>
        <Row className={styles.rowSmallSpace}>
          <Col md={12}>
            <Typography variant="smallHeading">
              {t("childDataStep.siblings")}
            </Typography>
          </Col>
        </Row>
        {child?.siblings?.map((sibling) => {
          return (
            <React.Fragment key={sibling.oib}>
              <Row className={styles.rowLargeSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    value={sibling.firstName}
                    helperText={t("common.birthRegistry")}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    value={sibling.lastName}
                    helperText={t("common.birthRegistry")}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.oib")}
                    value={sibling.oib}
                    helperText={t("common.oibApi")}
                    plaintext
                    bold
                  />
                </Col>
              </Row>
              <Row className={styles.rowLargeSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.dateOfBirth")}
                    value={hrDate(sibling.dateOfBirth)}
                    helperText={t("common.birthRegistry")}
                    plaintext
                    bold
                  />
                </Col>
              </Row>
            </React.Fragment>
          );
        })}
      </>
    ) : null;
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <InfoPanel
            message={t("person.mainInfoMessage")}
            color="success"
            className={styles.mainInfoMessage}
            fullWidth
          />
        </Col>
      </Row>
      <Row className={styles.rowSmallSpace}>
        <Col md={12}>
          <Typography component="h2" variant="smallHeading">
            {t("childDataStep.basicInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.firstName")}
            helperText={t("common.birthRegistry")}
            value={child?.firstName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.lastName")}
            helperText={t("common.birthRegistry")}
            value={child?.lastName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.oib")}
            helperText={t("common.oibApi")}
            value={child?.oib || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.dateOfBirth")}
            helperText={t("common.birthRegistry")}
            value={
              child && child.dateOfBirth
                ? hrDate(child?.dateOfBirth)
                : emptyValue
            }
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.sex")}
            helperText={t("common.birthRegistry")}
            value={child?.sexName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.birthplace")}
            helperText={t("common.birthRegistry")}
            value={child?.birthplaceName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.countryBirth")}
            helperText={t("common.birthRegistry")}
            value={child?.countryOfBirthName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={t("person.citizenship")}
            helperText={t("common.birthRegistry")}
            value={child?.citizenshipName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <Select
            emptyOption={{ value: undefined, text: "" }}
            label={t("person.nationalMinority")}
            name={nameof<typeof child>("nationalMinorityId")}
            value={child?.nationalMinorityId}
            onChange={(e) =>
              handleMultipleChange(
                e,
                nameof<CodeBook>("id"),
                nationalMinorities,
                [
                  {
                    valueProp: nameof<CodeBook>("id"),
                    stateProp: nameof<typeof child>("nationalMinorityId"),
                  },
                  {
                    valueProp: nameof<CodeBook>("name"),
                    stateProp: nameof<typeof child>("nationalMinorityName"),
                  },
                ],
                setChild
              )
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: nationalMinorities || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={4}>
          <Select
            required
            label={t("person.nationality")}
            name={nameof<typeof child>("nationalityId")}
            value={child?.nationalityId}
            onChange={(e) =>
              handleMultipleChange(
                e,
                nameof<CodeBook>("id"),
                nationalities,
                [
                  {
                    valueProp: nameof<CodeBook>("id"),
                    stateProp: nameof<typeof child>("nationalityId"),
                  },
                  {
                    valueProp: nameof<CodeBook>("name"),
                    stateProp: nameof<typeof child>("nationalityName"),
                  },
                ],
                setChild
              )
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: nationalities || [],
              } as SelectData<CodeBook>
            }
            errorText={errors?.nationalityId}
          />
        </Col>
      </Row>
      {renderChildSiblings()}
      <Row className={styles.rowSmallSpace}>
        <Col md={12}>
          <Typography component="h2" variant="smallHeading">
            {t("person.permanentResidenceInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowSmallSpace}>
        <Col md={12}>
          {(child.integrationResultStatus &
            IntegrationResultStatusEnum.OibFailed) ===
          IntegrationResultStatusEnum.OibFailed ? (
            <InfoPanel
              message={t("person.childPermanentResidenceError")}
              color="danger"
              className={styles.errorMessage}
            />
          ) : (
            <InfoPanel
              message={t("common.mupResidenceInfoMessage")}
              color="success"
            />
          )}
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.country")}`}
            value={child?.permanentResidence?.countryName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.municipality"
            )}`}
            value={child?.permanentResidence?.municipalityName || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.town")}`}
            value={child?.permanentResidence?.townName || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.postalCode"
            )}`}
            value={child?.permanentResidence?.townPostalCode || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t("person.street")}`}
            value={child?.permanentResidence?.street || emptyValue}
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.streetNumber"
            )}`}
            value={child?.permanentResidence?.streetNumber || emptyValue}
            plaintext
            bold
          />
        </Col>
      </Row>
      <Row className={styles.rowLargeSpace}>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.streetNumberAddition"
            )}`}
            value={
              child?.permanentResidence?.streetNumberAddition || emptyValue
            }
            plaintext
            bold
          />
        </Col>
        <Col md={4}>
          <TextField
            type="text"
            label={`${t("person.permanentResidence")} - ${t(
              "person.dateFrom"
            )}`}
            value={
              child?.permanentResidence?.dateFrom
                ? hrDate(child.permanentResidence.dateFrom)
                : emptyValue
            }
            plaintext
            bold
          />
        </Col>
      </Row>

      {child?.temporaryResidence && (
        <>
          <Row className={styles.rowSmallSpace}>
            <Col md={12}>
              <Typography variant="smallHeading" component="h2">
                {t("person.temporaryResidenceInformation")}
              </Typography>
            </Col>
          </Row>
          <Row className={styles.rowSmallSpace}>
            <Col md={12}>
              {(child.integrationResultStatus &
                IntegrationResultStatusEnum.MupFailed) ===
              IntegrationResultStatusEnum.MupFailed ? (
                <InfoPanel
                  message={t("person.childTemporaryResidenceError")}
                  color="danger"
                  className={styles.errorMessage}
                />
              ) : (
                <InfoPanel
                  message={t("common.mupResidenceInfoMessage")}
                  color="success"
                />
              )}
            </Col>
          </Row>
          <Row className={styles.rowLargeSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.country"
                )}`}
                value={child?.temporaryResidence?.countryName || emptyValue}
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.municipality"
                )}`}
                value={
                  child?.temporaryResidence?.municipalityName || emptyValue
                }
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.town"
                )}`}
                value={child?.temporaryResidence?.townName || emptyValue}
                plaintext
                bold
              />
            </Col>
          </Row>
          <Row className={styles.rowLargeSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.postalCode"
                )}`}
                value={child?.temporaryResidence?.townPostalCode || emptyValue}
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.street"
                )}`}
                value={child?.temporaryResidence?.street}
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.streetNumber"
                )}`}
                value={child?.temporaryResidence?.streetNumber || emptyValue}
                plaintext
                bold
              />
            </Col>
          </Row>
          <Row className={styles.rowLargeSpace}>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.streetNumberAddition"
                )}`}
                value={
                  child?.temporaryResidence?.streetNumberAddition || emptyValue
                }
                plaintext
                bold
              />
            </Col>
            <Col md={4}>
              <TextField
                type="text"
                label={`${t("person.temporaryResidence")} - ${t(
                  "person.dateFrom"
                )}`}
                value={
                  child?.temporaryResidence?.dateFrom
                    ? hrDate(child.temporaryResidence.dateFrom)
                    : emptyValue
                }
                plaintext
                bold
              />
            </Col>
          </Row>
        </>
      )}

      <Row className={styles.rowSmallSpace}>
        <Col md={12}>
          <Typography component="h2" variant="smallHeading">
            {t("documentUpload.additionalChildDocumentation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowSmallSpace}>
        <Col md={12}>
          <InfoPanel
            message={t("documentUpload.infoMessage")}
            color="success"
            className={styles.mainInfoMessage}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <Button
            color="primary"
            disabled={
              child.documents && child.documents.length >= 10 ? true : false
            }
            outline
            icon={<UploadIcon />}
            onClick={handleToggleDocumentsModal}
          >
            {t("documentUpload.additionalDocumentationAttach")}
          </Button>
          <DocumentsModal
            open={isDocumentsModalOpen}
            allDocuments={allDocuments}
            onClose={handleCloseDocumentsModal}
            onConfirm={handleConfirmDocumentsModal}
            onUpload={handleUploadDocuments}
            onRemove={handleRemoveDocument}
            onDownload={handleDownloadDocument}
            onExistingDocumentClick={handleUploadExistingDocument}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <Col md={12}>
            <Typography variant="smallHeading">
              {t("documentUpload.additionalDocumentationList")}
            </Typography>
          </Col>
          <DocumentList
            documents={child?.documents || []}
            onRemoveClick={handleRemoveDocumentFromList}
            onDocumentClick={handleDownloadDocument}
            fullWidth
          />
        </Col>
      </Row>
    </>
  );
}
